import { defineAsyncComponent } from "vue";
import { Form } from "ant-design-vue";
import {
    createColumnInlineInput,
    createColumnInlineRadioGroup,
    createColumnInlineSelect,
    createColumnInput
} from "../../../../../../components/table/c-table.logic";
import i18n from "../../../../../../i18n";
import { createLoading } from "../../../../../../utils/loading";
import store from "../../../../../../store";
import lodash from "lodash";
import { conditionFormChecker, putField } from "../../../../../../utils/form";
import {
    createFieldDate,
    createFieldInput,
    createFieldInputNumber
} from "../../../../../../components/input/template-builder/template-builder.logic";

const CSelect = defineAsyncComponent(() => import("../../../../../../components/input/template-builder/select/CSelect"));

export const { isLoading, setIsLoading } = createLoading();
export const actionPrefix = "resource/v1/transaction/samplingAnalysisProduct";
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callSamplingMethod = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_METHOD", value: searchQuery });
export const callSamplingTopic = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_TOPIC", value: searchQuery });
export const callResultType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "SAMPLING_ANALYSIS_RESULT", value: searchQuery });
export const callQuantityUnit = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "QUANTITY_UNIT", value: searchQuery });

export const createColumns = ({ props, data }) => {
    const { viewOnly, itemsKey } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    const columns = [];

    columns.push(
        createColumnInput({
            title: () => i18n.global.t("model.sampling.approveCode"),
            dataIndex: "refSendSamplingProduct.refSampling.approveCode",
            align: "center"
        }),
        createColumnInput({
            title: () => i18n.global.t("views.request.sendSamplingProductInfo.refHazObject"),
            dataIndex: "refSendSamplingProduct.refSampling.refReleaseProduct.refInspectionProduct.refInformProduct.refHazObject.nameTh",
            align: "center"
        }),
        createColumnInlineSelect({
            title: () => i18n.global.t("model.samplingAnalysisProduct.refTopic"),
            dataIndex: "refTopic",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: callSamplingTopic, optLabel: "value" }),
            search: { name: "refTopic.id" },
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly,
                        list: callSamplingTopic,
                        optLabel: "value",
                        "onUpdate:value": value => {
                            record.refTopic = value;
                            record.isSave = true;
                        },
                        allowClear: true,
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            width: 200,
        }),
        createColumnInlineSelect({
            title: () => i18n.global.t("model.samplingAnalysisProduct.refMethod"),
            dataIndex: "refMethod",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: callSamplingMethod, optLabel: "value" }),
            search: { name: "refMethod.id" },
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly,
                        list: callSamplingMethod,
                        optLabel: "value",
                        "onUpdate:value": value => {
                            record.refMethod = value;
                            record.isSave = true;
                        },
                        allowClear: true,
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                )
        }),
        createColumnInlineInput({
            title: () => i18n.global.t("model.samplingAnalysisProduct.testResult"),
            dataIndex: "testResult",
            items,
            useForm,
            itemsKey,
            customRenderInputProp: ({ key }) =>
                lodash.merge({ disabled: viewOnly }, conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } }))
        }),
        createColumnInlineInput({
            title: () => i18n.global.t("model.samplingAnalysisProduct.criterionResult"),
            dataIndex: "criterionResult",
            items,
            useForm,
            itemsKey,
            customRenderInputProp: ({ key }) =>
                lodash.merge({ disabled: viewOnly }, conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } }))
        }),
        createColumnInlineInput({
            title: () => i18n.global.t("model.samplingAnalysisProduct.topicUnit"),
            dataIndex: "topicUnit",
            items,
            useForm,
            itemsKey,
            customRenderInputProp: ({ key }) =>
                lodash.merge({ disabled: viewOnly }, conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } }))
        }),
        createColumnInlineRadioGroup({
            title: () => i18n.global.t("views.request.releaseResultProduct.refResult"),
            dataIndex: "refResult",
            items,
            useForm,
            itemsKey,
            width: 180,
            inputProp: () => ({ list: callResultType, optLabel: "value" }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly,
                        list: callResultType,
                        optLabel: "value",
                        type: "vertical",
                        "onUpdate:value": value => {
                            record.refResult = value;
                            record.isSave = true;
                            if (lodash.some(items, d => !d.refResult)) {
                                modelRef.refSamplingAnalysisTemp.refResult = null;
                            } else if (lodash.every(items, d => d.refResult?.key === "1")) {
                                modelRef.refSamplingAnalysisTemp.refResult = data.resultTypes?.find(d => d.key === "1");
                            } else if (lodash.some(items, d => d.refResult?.key === "2")) {
                                modelRef.refSamplingAnalysisTemp.refResult = data.resultTypes?.find(d => d.key === "2");
                            }
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
        })
    );
    return columns;
}

export const createField = ({ props, data, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return putField({
        data,
        fields: [
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}refSendSamplingProduct.refSampling.refReleaseProduct.refInspectionProduct.refInformProduct.refHaz.refSubStanceTypeMain.value`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.sendSamplingProductInfo.refSubStanceType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
            },
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}analysisNo`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.samplingAnalysisProduct.analysisNo"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.analysisNo,
                            "onUpdate:value": value => {
                                model.analysisNo = value;
                                model.isSave = true;
                            },
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}receiveDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.samplingAnalysisProduct.receiveDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.receiveDate,
                            "onUpdate:value": value => {
                                model.receiveDate = value;
                                model.isSave = true;
                            },
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}refSendSamplingProduct.refSampling.approveCode`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.sampling.approveCode"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}analysisDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.samplingAnalysisProduct.analysisDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.analysisDate,
                            "onUpdate:value": value => {
                                model.analysisDate = value;
                                model.isSave = true;
                            },
                            disabled: viewOnly
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}refSendSamplingProduct.refSampling.refReleaseProduct.refInspectionProduct.refInformProduct.refHazObject.nameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.sendSamplingProductInfo.refHazObject"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}refSendSamplingProduct.refSampling.refReleaseProduct.refInspectionProduct.refInformProduct.refHaz.tradeNameTh`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.sendSamplingProductInfo.tradeNameTh"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}refSendSamplingProduct.refSampling.refReleaseProduct.refInspectionProduct.refInformProduct.refHaz.approveCode`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.sendSamplingProductInfo.refHaz"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}refSendSamplingProduct.refSampling.manufacturer`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.sendSamplingProductInfo.manufacturer"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}refSendSamplingProduct.refSampling.importer`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.sendSamplingProductInfo.importer"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            createFieldDate({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refSendSamplingProduct.refSampling.manufactureDate`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.sendSamplingProductInfo.manufactureDate"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldInput({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}refSendSamplingProduct.refSampling.refReleaseProduct.refInspectionProduct.refInformProduct.refHaz.packages`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("views.request.samplingAnalysisProductInfo.packages"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: lodash.get(modelRef, field?.key)?.map(d=>d.refPackage1?.value)?.join(", "),
                            disabled: true
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            {
                visible: () => true,
                colProp: () => ({ xs: 24 }),
            },
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}size`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.sampling.size"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model?.size,
                            "onUpdate:value": value => {
                                model.size = value;
                                model.isSave = true;
                            },
                            disabled: viewOnly,
                            allowPrecision: true,
                            addonAfter: () => {
                                const addonAfterProp = lodash.merge(
                                    {
                                        value: model?.refSizeUnit,
                                        "onUpdate:value": value => {
                                            model.refSizeUnit = value;
                                            model.isSave = true;
                                        },
                                        disabled: true,
                                        list: callQuantityUnit,
                                        optLabel: "value",
                                        placeholder: i18n.global.t("button.unit"),
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}refSizeUnit` }, model }
                                    })
                                );
                                return (
                                    <Form.Item {...useForm.validateInfos[`${prefix}refSizeUnit`]} help={undefined}>
                                        <CSelect {...addonAfterProp}></CSelect>
                                    </Form.Item>
                                );
                            }
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
            createFieldInputNumber({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field, model } }),
                key: `${prefix}qty`,
                colProp: () => ({ xs: 24, md: 12 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.sampling.qty"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model?.qty,
                            "onUpdate:value": value => {
                                model.qty = value;
                                model.isSave = true;
                            },
                            disabled: viewOnly,
                            allowPrecision: true,
                            addonAfter: () => {
                                const addonAfterProp = lodash.merge(
                                    {
                                        value: model?.refQtyUnit,
                                        "onUpdate:value": value => {
                                            model.refQtyUnit = value;
                                            model.isSave = true;
                                        },
                                        disabled: true,
                                        list: callQuantityUnit,
                                        optLabel: "value",
                                        placeholder: i18n.global.t("button.unit"),
                                        style: { width: "100px !important" }
                                    },
                                    conditionFormChecker({
                                        props,
                                        data,
                                        executeKey: "inputPropField",
                                        params: { field: { key: `${prefix}refQtyUnit` }, model }
                                    })
                                );
                                return (
                                    <Form.Item {...useForm.validateInfos[`${prefix}refQtyUnit`]} help={undefined}>
                                        <CSelect {...addonAfterProp}></CSelect>
                                    </Form.Item>
                                );
                            }
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field, model } })
                    )
            }),
        ]
    });
}